import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Button } from "../../../components/ui/button";
import { MessageCircle, Send, X, ChevronRight, ShoppingCart, UserPlus, Clock } from "lucide-react";
import MessageBubble from "../generalChat/chatSidebar/messageBubble";
import { Textarea } from "../../../components/ui/textarea";
import { FormattingToolbar } from "../FormattingToolbar";
import { insertMarkdown, handleMarkdownShortcut } from "../../../lib/markdownUtils";
import { Badge } from "../../../components/ui/badge";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { useSocketContext } from "../../../context/SocketProvider";
import { config } from "../../../Environment";
import { toast } from "sonner";
import { useInView } from "react-intersection-observer";
import { useSocket, SocketMessage } from "../../../hooks/useSocket";
import { TypingIndicator } from "../generalChat/TypingIndicator";
import { OnlineStatusIndicator } from "../generalChat/chatSidebar/chatView/OnlineStatusIndicator";
import useAuth from "../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { useDebounce } from "use-debounce";
import { currencyAmount } from "../../../lib/currency";
import { PaymentIframe } from "../../payment/PaymentIframe";
import { Spinner } from "../../ui/spinner";
import { PageLoader } from "../../ui/page-loader";

// Use the SocketMessage interface from the hook
type Message = SocketMessage;

interface ProcessChatProps {
  processId: string;
  isMobile: boolean;
  paneReference?: {
    screen: string;
    screenLabel: string;
    component?: string;
    componentKey?: string;
  } | null;
  onPaneReferenceUsed?: () => void;
  onPaneReferenceClick?: (reference: {
    screen: string;
    screenLabel: string;
    component?: string;
    componentKey?: string;
  }) => void;
  otherUser?: {
    _id: string;
    displayName: string;
  } | null;
  isMentorJoined?: boolean;
  unlimitedMentoring?: boolean;
  onCollapse?: () => void;
  onMentorJoined?: () => void;
}

function ProcessChat({
  processId,
  isMobile,
  paneReference,
  onPaneReferenceUsed,
  onPaneReferenceClick,
  otherUser: propOtherUser,
  isMentorJoined: propIsMentorJoined,
  unlimitedMentoring: propUnlimitedMentoring,
  onCollapse,
  onMentorJoined,
}: ProcessChatProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "he";
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [currentPaneReference, setCurrentPaneReference] = useState<{
    screen: string;
    screenLabel: string;
    component?: string;
    componentKey?: string;
  } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [chatCreationFailed, setChatCreationFailed] = useState(false);
  const [otherUser, setOtherUser] = useState<{
    _id: string;
    displayName: string;
  } | null>(propOtherUser || null);
  const [isMentorJoined, setIsMentorJoined] = useState<boolean | null>(null);
  const [unlimitedMentoring, setUnlimitedMentoring] = useState<boolean>(false);
  const [isLoadingProcess, setIsLoadingProcess] = useState(false);
  const [processData, setProcessData] = useState<any>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [otherTypingUserName, setOtherTypingUsername] = useState<string | null>(
    null
  );
  const [isOtherUserTyping, setIsOtherUserTyping] = useState(false);
  // Debounced values for typing detection
  const [debouncedNewMessage] = useDebounce(newMessage, 300);
  const [debouncedTypingStop] = useDebounce(
    newMessage.trim() ? "" : "stop",
    1000
  );

  // Mentoring-related state
  const [isLoadingMentoring, setIsLoadingMentoring] = useState(false);
  const [isLoadingMentoringData, setIsLoadingMentoringData] = useState(false);
  const [process, setProcess] = useState<any>(null);
  const [vendor, setVendor] = useState<any>(null);
  const [linkClientExpert, setLinkClientExpert] = useState<any>(null);
  const [isMentorInvited, setIsMentorInvited] = useState<boolean>(false);
  const [mentorCredits, setMentorCredits] = useState<number>(0);
  const [mentorProducts, setMentorProducts] = useState<any[]|null>(null);
  const [mentorMessage, setMentorMessage] = useState<string>("");
  const [paymentUrl, setPaymentUrl] = useState<string>("");
  const [showPaymentIframe, setShowPaymentIframe] = useState<boolean>(false);
  const [showMentoringForm, setShowMentoringForm] = useState<boolean>(false);
  const [mentoringDataError, setMentoringDataError] = useState<string | null>(null);

  const retryFetch = useRetryFetch();
  const { auth } = useAuth();
  const { socket: rawSocket } = useSocketContext();

  // Socket.IO integration
  const {
    isConnected,
    isConnecting,
    error: socketError,
    // typingUsers,
    joinChat,
    leaveChat,
    sendTypingStart,
    sendTypingStop,
    // debouncedTypingStop,
    sendMessage,
    markMessageAsRead,
    reconnect,
  } = useSocket();

  // Get current user ID from JWT token
  const getCurrentUserId = useCallback(() => {
    if (!auth.accessToken) return null;
    try {
      const payload = JSON.parse(atob(auth.accessToken.split(".")[1]));
      const userId = payload.userId || payload.sub;
      return userId;
    } catch (error) {
      console.error("Error decoding JWT token:", error);
      return null;
    }
  }, [auth.accessToken]);

  const currentUserId = useMemo(() => getCurrentUserId(), [getCurrentUserId]);

  // Mentoring functions
  async function getProcessInfo() {
    try {
      setIsLoadingMentoringData(true);
      setMentoringDataError(null);

      console.log(`[ProcessChat] getProcessInfo processId ${processId}`);
      const response = await retryFetch(
        `${config.API_BASE}/api/processes/${processId}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch process: ${response.status}`);
      }

      const processInfo = await response.json();
      console.log(`[ProcessChat] Process info loaded:`, processInfo);

      setProcess(processInfo);
      setIsMentorInvited(processInfo?.isMentorInvited ?? false);
      setIsMentorJoined(processInfo?.isMentorJoined ?? false);

      // Load expert and product data
      await Promise.all([
        getLinkClientExpert(processInfo),
        getMentorProducts(processInfo)
      ]);
    } catch (error: any) {
      console.error(`[ProcessChat] Error loading process info:`, error);
      setMentoringDataError(error.message || "Failed to load mentoring information");
      toast.error("Failed to load mentoring information");
    } finally {
      setIsLoadingMentoringData(false);
    }
  }

  async function getLinkClientExpert(processInfo: any) {
    try {
      console.log(`[ProcessChat] getLinkClientExpert`, processInfo?.fiExpert);
      const expertId = processInfo?.fiExpert?._id?.toString();

      if (!expertId) {
        console.warn(`[ProcessChat] No expert ID found in process`);
        return;
      }

      const response = await retryFetch(
        `${config.API_BASE}/api/user/mentor/search?expertId=${encodeURIComponent(expertId)}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch expert info: ${response.status}`);
      }

      const responseMessage = await response.json();
      console.log(`[ProcessChat] Expert info response:`, responseMessage);

      if (Array.isArray(responseMessage.list) && responseMessage.list.length > 0) {
        const linkClientExpertRecord = responseMessage.list[0];
        setLinkClientExpert(linkClientExpertRecord);
        setUnlimitedMentoring(linkClientExpertRecord.unlimitedMentoring ?? false);

        if (typeof linkClientExpertRecord.fiVendor === 'object' && linkClientExpertRecord.fiVendor !== null) {
          setVendor(linkClientExpertRecord.fiVendor);
        } else {
          setVendor(null);
        }

        const credits = linkClientExpertRecord.availableMentorSessionCredits ?? 0;
        setMentorCredits(credits);
        console.log(`[ProcessChat] Expert credits: ${credits}, unlimited: ${linkClientExpertRecord.unlimitedMentoring}`);
      }
    } catch (error: any) {
      console.error(`[ProcessChat] Error loading expert info:`, error);
    }
  }

  async function getMentorProducts(processInfo: any) {
    try {
      if (!processInfo?.fiExpert) {
        console.warn(`[ProcessChat] No expert in process, skipping product fetch`);
        return;
      }

      const response = await retryFetch(
        `${config.API_BASE}/api/product/searchActive?type=mentor_session_credit&expertId=${encodeURIComponent(processInfo.fiExpert._id.toString())}&includeVendor=1`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch mentor products: ${response.status}`);
      }

      const responseMessage = await response.json();
      console.log(`[ProcessChat] Mentor products response:`, responseMessage);

      if (Array.isArray(responseMessage.list) && responseMessage.list.length > 0) {
        setMentorProducts(responseMessage.list);
      } else {
        setMentorProducts([]);
      }
    } catch (error: any) {
      console.error(`[ProcessChat] Error loading mentor products:`, error);
      setMentorProducts([]);
    }
  }

  async function buyMentorCredits(idx: number) {
    try {
      if (mentorProducts === null || mentorProducts[idx] === null) {
        toast.error(t("error"));
        return;
      }
      setIsLoadingMentoring(true);
      const response = await retryFetch(
        `${config.API_BASE}/api/payment/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            productId: mentorProducts[idx]._id.toString(),
            useIframe: true,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.type === 'redirect' && result.redirect) {
          console.log(`[ProcessChat] Opening payment in iframe: ${result.redirect}`);
          setPaymentUrl(result.redirect);
          setShowPaymentIframe(true);
        } else {
          toast.error(t("payment_initialization_failed") || "Failed to initialize payment");
        }
      } else {
        toast.error(t("payment_initialization_failed") || "Failed to initialize payment");
      }
    } catch (err: any) {
      console.error(`[ProcessChat] Buy mentor credits failed:`, err);
      toast.error(t("payment_failed") || "Failed to start payment");
    } finally {
      setIsLoadingMentoring(false);
    }
  }

  async function handlePaymentSuccess() {
    console.log("[ProcessChat] Payment successful - refreshing credits");
    setShowPaymentIframe(false);
    toast.success(t("payment_successful_credits_added") || "Payment successful! Credits added.");

    // Add a small delay to ensure backend has processed the payment
    await new Promise(resolve => setTimeout(resolve, 1500));

    if (process) {
      console.log("[ProcessChat] Reloading expert info to get updated credits...");
      // Reload the expert info to get updated credits
      await getLinkClientExpert(process);
      console.log("[ProcessChat] Credits reloaded - new balance:", mentorCredits);
    }
  }

  function handlePaymentError(error: string) {
    toast.error(error || t("payment_failed") || "Payment failed");
    setShowPaymentIframe(false);
  }

  function handlePaymentClose() {
    setShowPaymentIframe(false);
  }

  async function inviteMentor() {
    try {
      if (!process?.fiExpert) {
        toast.error(t("no_mentor_information_available"));
        return;
      }

      setIsLoadingMentoring(true);
      const response = await retryFetch(
        `${config.API_BASE}/api/process/${processId}/component/invite-mentor`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            expertId: process.fiExpert._id.toString(),
            message: mentorMessage,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to invite mentor: ${response.status}`);
      }

      const data = await response.json();
      console.log(`[ProcessChat] Invite mentor response:`, data);

      if (data.type === 'status' && data.status === 'OK') {
        setIsMentorInvited(true);
        toast.success(t("mentor_invitation_sent_successfully"));

        // If unlimited mentoring, mentor joins immediately
        if (unlimitedMentoring) {
          setIsMentorJoined(true);
        }

        // Send invitation message as the first chat message
        if (mentorMessage.trim()) {
          try {
            const newChatId = await createOrGetProcessChat();
            if (newChatId) {
              const msgResponse = await retryFetch(
                `${config.API_BASE}/api/chats/${newChatId}/messages`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({ content: mentorMessage.trim() }),
                }
              );
              if (msgResponse.ok) {
                const msgData = await msgResponse.json();
                setMessages((prev) => {
                  if (prev.some((m) => m._id === msgData.item._id)) return prev;
                  return [msgData.item, ...prev];
                });
                console.log("[ProcessChat] Invitation message sent as first chat message");
              }
            }
          } catch (chatErr) {
            console.warn("[ProcessChat] Could not send invitation message to chat:", chatErr);
            // Non-critical — invitation was already sent via the API
          }
        }
      } else {
        throw new Error("Unexpected response from server");
      }
    } catch (err: any) {
      console.error(`[ProcessChat] Invite mentor failed:`, err);
      toast.error(err.message || t("failed_to_send_mentor_invitation"));
    } finally {
      setIsLoadingMentoring(false);
    }
  }

  // Mentor approves/joins the mentoring request
  async function approveMentoringRequest() {
    try {
      setIsLoadingMentoring(true);
      const response = await retryFetch(
        `${config.API_BASE}/api/mentor/process/${processId}/join`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 409) {
          toast.error(t("insufficient_credits_to_join"));
        } else {
          throw new Error(`Failed to approve mentoring request: ${response.status}`);
        }
        return;
      }

      const data = await response.json();
      console.log(`[ProcessChat] Approve mentoring response:`, data);

      if (data.type === 'status' && data.status === 'OK') {
        setIsMentorJoined(true);
        toast.success(t("mentoring_request_approved"));

        // Notify parent component that mentor has joined
        onMentorJoined?.();

        // Note: Don't create chat here - the useEffect will handle it
        // when isMentorJoined changes to true
      } else {
        throw new Error("Unexpected response from server");
      }
    } catch (err: any) {
      console.error(`[ProcessChat] Approve mentoring failed:`, err);
      toast.error(err.message || t("failed_to_approve_mentoring_request"));
    } finally {
      setIsLoadingMentoring(false);
    }
  }

  // Check if current user is the mentor
  const isMentor = useMemo(() => {
    const data = processData || process;
    if (!data || !auth?.email) return false;
    return data.fiExpert?.email === auth.email;
  }, [processData, process, auth?.email]);

  // Check if current user is running their own workflow (both initiator and expert)
  const isOwnWorkflow = useMemo(() => {
    const data = processData || process;
    if (!data || !auth?.email) return false;
    return (
      data.fiInitiator?.email === auth.email &&
      data.fiExpert?.email === auth.email
    );
  }, [processData, process, auth?.email]);

  // Get other user info from process data based on current user's email
  const getOtherUserInfo = (processData: any) => {
    if (!processData || !auth?.email) return null;

    const currentUserEmail = auth.email;

    // Check if current user is the mentor (fiExpert)
    if (
      processData.fiExpert &&
      processData.fiExpert.email === currentUserEmail
    ) {
      // Current user is mentor, show mentee info (fiInitiator)
      return processData.fiInitiator
        ? {
            _id: processData.fiInitiator._id,
            displayName:
              processData.fiInitiator.displayName ||
              `${processData.fiInitiator.firstName || ""} ${
                processData.fiInitiator.lastName || ""
              }`.trim() ||
              processData.fiInitiator.email,
            email: processData.fiInitiator.email,
          }
        : null;
    }
    // Check if current user is the mentee (fiInitiator)
    else if (
      processData.fiInitiator &&
      processData.fiInitiator.email === currentUserEmail
    ) {
      // Current user is mentee, show mentor info (fiExpert)
      return processData.fiExpert
        ? {
            _id: processData.fiExpert._id,
            displayName:
              processData.fiExpert.displayName ||
              `${processData.fiExpert.firstName || ""} ${
                processData.fiExpert.lastName || ""
              }`.trim() ||
              processData.fiExpert.email,
            email: processData.fiExpert.email,
          }
        : null;
    }

    // If current user doesn't match either role, return null
    return null;
  };

  // Handle pane reference changes
  useEffect(() => {
    if (paneReference) {
      setCurrentPaneReference(paneReference);
      // Focus on the textarea when a pane is quoted
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [paneReference]);

  useEffect(() => {
    if (!isConnected || !chatId) return;

    // When debounced message changes and has content, send typing start

    if (debouncedNewMessage.trim() && !isTyping) {
      sendTypingStart(chatId);

      setIsTyping(true);
    }
  }, [
    debouncedNewMessage,
    isConnected,
    chatId,
    isTyping,
    sendTypingStart,
  ]);

  useEffect(() => {
    if (!isConnected || !chatId) return;

    // When debounced typing stop triggers and user is currently typin

    if (debouncedTypingStop === "stop" && isTyping) {
      sendTypingStop(chatId);

      setIsTyping(false);
    }
  }, [
    debouncedTypingStop,
    isConnected,
    chatId,
    isTyping,
    sendTypingStop,
  ]);

  // Handler to load mentoring data
  const handleRequestMentoring = async () => {
    setShowMentoringForm(true);

    // If we don't have expert/product data yet, load it
    // Check for linkClientExpert or mentorProducts, not just process
    if (!linkClientExpert || mentorProducts === null) {
      await getProcessInfo();
    }
  };

  // Fetch process data on mount
  useEffect(() => {
    if (!processId) return;

    const fetchData = async () => {
      setIsLoadingProcess(true);
      try {
        const response = await retryFetch(
          `${config.API_BASE}/api/processes/${processId}`
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch process data: ${response.status}`);
        }

        const data = await response.json();
        setProcessData(data);
        setProcess(data);
        setIsMentorInvited(data?.isMentorInvited ?? false);

        // If mentor status was provided via props, use it; otherwise use data from API
        if (propIsMentorJoined !== undefined) {
          setIsMentorJoined(propIsMentorJoined);
          setUnlimitedMentoring(propUnlimitedMentoring || false);
        } else {
          setIsMentorJoined(data?.isMentorJoined ?? false);
        }

        // Determine other user based on current user's role (only if not provided via props)
        if (!propOtherUser) {
          const otherUserInfo = getOtherUserInfo(data);
          setOtherUser(otherUserInfo);
        }
      } catch (error) {
        console.error("Error fetching process data:", error);
        setIsMentorJoined(false);
      } finally {
        setIsLoadingProcess(false);
      }
    };

    fetchData();
  }, [processId, propIsMentorJoined, propUnlimitedMentoring]); // Removed retryFetch from dependencies

  // Listen for real-time mentor acceptance notifications
  useEffect(() => {
    if (!rawSocket || !processId) return;

    const handleMentorAccepted = (data: {
      processId: string;
      expertId: string;
      expertName: string;
    }) => {
      // Check if this event is for our current process
      if (data.processId === processId) {
        console.log(`[ProcessChat] Mentor ${data.expertName} accepted the request`);

        // Update the local state to reflect mentor has joined
        setIsMentorJoined(true);

        // Show success notification
        toast.success(`${data.expertName} has joined your session!`, {
          duration: 5000,
        });

        // Note: Don't create chat here - the useEffect will handle it
        // when isMentorJoined changes to true

        // Catchup re-fetch: after mentor accepts, re-fetch messages to catch
        // any messages sent during the join race window
        const currentChatId = chatIdRef.current;
        if (currentChatId) {
          setTimeout(() => getChatMessages(currentChatId, 1), 1500);
        }
      }
    };

    rawSocket.on("mentor:accepted", handleMentorAccepted);

    return () => {
      rawSocket.off("mentor:accepted", handleMentorAccepted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSocket, processId, chatId, isCreatingChat]);

  const { ref: topRef, inView } = useInView({
    threshold: 0,
    rootMargin: "100px", // trigger before fully visible
  });

  // Create or get process chat
  const createOrGetProcessChat = useCallback(async () => {
    if (!processId || isCreatingChat || chatCreationFailed) return;

    // Allow chat creation when:
    // 1. User is running their own workflow (isOwnWorkflow)
    // 2. Mentor has joined (isMentorJoined)
    // 3. Mentor has been invited (isMentorInvited) - users can send messages while waiting
    // 4. Unlimited mentoring is enabled
    // 5. On /portal page (My Mentees tab) - no restrictions

    setIsCreatingChat(true);
    try {
      const response = await retryFetch(`${config.API_BASE}/api/chats`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          type: "process",
          processId: processId,
        }),
      });

      if (!response.ok) {
        let errorText = "";
        let errorJson: any = null;

        try {
          errorText = await response.text();
          errorJson = JSON.parse(errorText);
        } catch (e) {
          // errorText might not be JSON
        }

        console.error("❌ Chat creation failed:", response.status, errorText);

        // Check for auth errors (401, 403) - these won't be fixed by retrying
        if (response.status === 401 || response.status === 403) {
          console.error("🚫 Authentication error - stopping retry attempts");
          setChatCreationFailed(true);
          toast.error(t("authentication_error_please_refresh") || "Authentication error. Please refresh the page.");
          return;
        }

        // Check for 400 errors (bad request, process not found, etc.) - stop retrying
        if (response.status === 400) {
          console.error("🚫 Bad request - stopping retry attempts");
          setChatCreationFailed(true);
          toast.error(errorJson?.error || t("invalid_request") || "Invalid request");
          return;
        }

        // For 500 errors, check if it's a duplicate key error
        if (response.status === 500 && errorText.includes("duplicate key")) {
          console.warn("⚠️ Duplicate chat detected - fetching existing chat");
          // Chat already exists, fetch it instead
          try {
            const getResponse = await retryFetch(
              `${config.API_BASE}/api/chats?processId=${processId}&type=process`,
              {
                method: "GET",
                credentials: "include",
              }
            );

            if (getResponse.ok) {
              const data = await getResponse.json();
              if (data.item && data.item._id) {
                setChatId(data.item._id);
                setChatCreationFailed(false);
                console.log("✅ Retrieved existing chat:", data.item._id);
                return data.item._id;
              }
            }
          } catch (fetchError) {
            console.error("Failed to fetch existing chat:", fetchError);
          }

          // If we couldn't fetch the existing chat, stop trying
          setChatCreationFailed(true);
          toast.error(t("chat_already_exists") || "Chat already exists but couldn't be loaded. Please refresh the page.");
          return;
        }

        throw new Error(
          `Failed to create/get process chat: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();
      setChatId(data.item._id);
      setChatCreationFailed(false); // Reset on success

      // Mentor information is already set from process data, no need to fetch chat details

      return data.item._id;
    } catch (error: any) {
      console.error("Error creating/getting process chat:", error);

      // For network errors or other exceptions, don't retry indefinitely
      // Set a flag to prevent infinite loops
      const errorMessage = error?.message || String(error);

      if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
        console.error("🌐 Network error - stopping retry attempts");
        setChatCreationFailed(true);
        toast.error(t("network_error_check_connection") || "Network error. Please check your connection.");
      } else if (!chatCreationFailed) {
        // For other errors, show a generic message but don't set chatCreationFailed
        // This allows one retry but prevents infinite loops via the isCreatingChat flag
        toast.error(t("failed_to_create_chat"));
      }
    } finally {
      setIsCreatingChat(false);
    }
  }, [
    processId,
    isCreatingChat,
    chatCreationFailed,
    retryFetch,
    t,
    isMentorJoined,
    unlimitedMentoring,
    onPaneReferenceUsed,
    isOwnWorkflow,
  ]);

  // Fetch chat messages
  async function getChatMessages(chatId: string, page = 1, limit = 20) {
    try {
      const response = await retryFetch(
        `${config.API_BASE}/api/chats/${chatId}/messages?page=${page}&limit=${limit}`
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "❌ Failed to fetch messages:",
          response.status,
          errorText
        );
        throw new Error(
          `Failed to fetch messages: ${response.status} - ${errorText}`
        );
      }
      const data = await response.json();

      setMessages((prev: Message[]) => {
        // For first page, replace all messages
        if (page === 1) {
          return data.list || [];
        }

        // For subsequent pages, append new messages (older messages) with deduplication
        const existingIds = new Set(prev.map((m) => m._id));
        const newMessages = (data.list || []).filter(
          (msg: Message) => !existingIds.has(msg._id)
        );

        return [...prev, ...newMessages];
      });

      setHasMore(data.pagination.totalPages > data.pagination.page);

      return data;
    } catch (error) {
      console.error("Error fetching messages:", error);
      throw error;
    }
  }

  // Socket event handlers - Use useRef to avoid recreating the callback
  const currentUserIdRef = useRef(currentUserId);
  const chatIdRef = useRef(chatId);

  useEffect(() => {
    currentUserIdRef.current = currentUserId;
    chatIdRef.current = chatId;
  }, [currentUserId, chatId]);

  const handleNewMessage = useCallback(
    (message: Message) => {
      // Use refs to get latest values without recreating the callback
      const userId = currentUserIdRef.current;
      const activeChatId = chatIdRef.current;

      console.log('[ProcessChat] handleNewMessage called:', {
        messageId: message._id,
        messageSenderId: message.senderId,
        currentUserId: userId,
        chatId: activeChatId,
      });

      // Set the correct direction based on sender
      const logicalDirection =
        userId && message.senderId === userId
          ? "sent"
          : "received";

      console.log('[ProcessChat] Message direction:', logicalDirection);

      const messageWithCorrectDirection: Message = {
        ...message,
        direction: logicalDirection,
      };

      if (logicalDirection === "received") {
        setMessages((prev) => {
          // Deduplication check
          const isDuplicate = prev.some(m => m._id === message._id);
          console.log('[ProcessChat] Adding received message, isDuplicate:', isDuplicate);
          if (isDuplicate) {
            return prev;
          }
          setShouldScrollToBottom(true); // Enable scroll to bottom for new messages
          return [messageWithCorrectDirection, ...prev];
        });
      } else {
        console.log('[ProcessChat] Skipping sent message (should come from API response)');
      }

      // Mark message as read if it's from the other user
      if (message.senderId !== userId && activeChatId) {
        markMessageAsRead(message._id, activeChatId);
      }
    },
    [markMessageAsRead]
  );

  // const handleMessageStatusUpdate = useCallback(
  //   (data: { messageId: string; status: string; chatId: string }) => {
  //     if (data.chatId !== chatId) return;

  //     setMessages((prev) =>
  //       prev.map((msg) =>
  //         msg._id === data.messageId
  //           ? {
  //               ...msg,
  //               deliveryStatus: data.status as "sent" | "delivered" | "read",
  //               status: data.status as "sent" | "delivered" | "read",
  //             }
  //           : msg
  //       )
  //     );
  //   },
  //   [chatId]
  // );

  const messageReadStatusHandler = (data: {
    userId: string;
    chatId: string;
  }) => {
    console.log("Hello from read");
    setMessages((prevMessages) =>
      prevMessages.map((message) =>
        message.direction === "sent"
          ? { ...message, isMessageRead: true }
          : message
      )
    );
  };

  // Setup socket event listeners
  // IMPORTANT: Register message:new listener based on isConnected only (not chatId)
  // to avoid a race condition where messages sent between chat creation and
  // listener registration are lost. Use chatIdRef.current inside the handler.
  useEffect(() => {
    if (!isConnected) return;

    const socket = (window as any).socket;
    if (!socket) {
      console.log("Socket not available");
      return;
    }

    // Set up event listeners — filter by chatIdRef.current inside handlers
    const messageHandler = (message: Message) => {
      const activeChatId = chatIdRef.current;
      console.log('[ProcessChat] Socket message received:', {
        messageId: message._id,
        messageChatId: message.chatId,
        currentChatId: activeChatId,
        matches: message.chatId === activeChatId,
      });
      if (activeChatId && message.chatId === activeChatId) {
        handleNewMessage(message);
      } else {
        console.log('[ProcessChat] Message ignored - different chat or no chatId yet');
      }
    };

    const typingStatusHandler = (data: {
      chatId: string;
      userId: string;
      isTyping: boolean;
      username: string;
    }) => {
      const activeChatId = chatIdRef.current;
      if (activeChatId && data.chatId === activeChatId) {
        setIsOtherUserTyping(data.isTyping);
        setOtherTypingUsername(data.username);
      }
    };

    const readStatusHandler = (data: {
      userId: string;
      chatId: string;
    }) => {
      const activeChatId = chatIdRef.current;
      if (activeChatId && data.chatId === activeChatId) {
        setMessages((prevMessages) =>
          prevMessages.map((message) =>
            message.direction === "sent"
              ? { ...message, isMessageRead: true }
              : message
          )
        );
      }
    };

    // Add new listeners
    socket.on("message:new", messageHandler);
    socket.on("message:read", readStatusHandler);
    socket.on("typing_update", typingStatusHandler);

    return () => {
      // Clean up all listeners when component unmounts or connection changes
      socket.off("message:new", messageHandler);
      socket.off("message:read", readStatusHandler);
      socket.off("typing_update", typingStatusHandler);
    };
  }, [isConnected]); // Decoupled from chatId — uses chatIdRef.current inside handlers

  // Load messages when chat is created
  useEffect(() => {
    if (!chatId) return;

    const loadMessages = async () => {
      setShouldScrollToBottom(true); // Allow scroll to bottom for initial load
      setPage(1);
      setHasMore(true); // Keep as true, don't update based on API response (match General Chat)
      try {
        const data = await getChatMessages(chatId, 1);

        // Fallback: if no messages exist but there's an invitation message,
        // auto-send it as the first chat message (covers page-refresh and MentoringDialog paths)
        const loadedMessages = data?.list || [];
        const invitationMsg = processData?.invitationMessage;
        if (loadedMessages.length === 0 && invitationMsg && invitationMsg.trim()) {
          try {
            const msgResponse = await retryFetch(
              `${config.API_BASE}/api/chats/${chatId}/messages`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ content: invitationMsg.trim() }),
              }
            );
            if (msgResponse.ok) {
              const msgData = await msgResponse.json();
              setMessages((prev) => {
                if (prev.some((m) => m._id === msgData.item._id)) return prev;
                return [msgData.item, ...prev];
              });
              console.log("[ProcessChat] Auto-sent invitation message on load");
            }
          } catch (e) {
            console.warn("[ProcessChat] Could not auto-send invitation message:", e);
          }
        }
      } catch (error) {
        console.error("❌ Error loading messages:", error);
        toast.error(t("failed_to_load_messages"));
      }
    };

    loadMessages();
  }, [chatId, t]); // Removed getChatMessages from dependencies

  // Join chat room when chat is created
  useEffect(() => {
    if (isConnected && chatId) {
      joinChat(chatId);
    }

    return () => {
      if (isConnected && chatId) {
        leaveChat(chatId);
      }
    };
  }, [isConnected, chatId, joinChat, leaveChat]);

  // Trigger next page when top sentinel is in view
  useEffect(() => {
    if (inView && hasMore && !isLoading && chatId) {
      setIsLoading(true);
      setShouldScrollToBottom(false); // Prevent scroll to bottom during pagination
      const nextPage = page + 1;

      getChatMessages(chatId, nextPage)
        .then((data) => {
          setPage(nextPage);
          // Don't update hasMore - keep it as true to match General Chat behavior
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error loading more messages:", error);
          toast.error(t("failed_to_load_messages"));
          setIsLoading(false);
        });
    }
  }, [inView]);

  // Initialize chat when processId is available
  useEffect(() => {
    if (
      processId &&
      !chatId &&
      !isCreatingChat &&
      !chatCreationFailed &&
      (isMentorJoined || isMentorInvited || unlimitedMentoring || isOwnWorkflow)
    ) {
      createOrGetProcessChat();
    }
  }, [
    processId,
    chatId,
    isCreatingChat,
    chatCreationFailed,
    createOrGetProcessChat,
    isMentorJoined,
    isMentorInvited,
    unlimitedMentoring,
    isOwnWorkflow,
  ]);

  // Reset scroll behavior when chatId changes
  useEffect(() => {
    if (chatId) {
      setShouldScrollToBottom(true); // Allow scroll to bottom when switching chats
      setHasMore(true); // Reset hasMore when switching chats
    }
  }, [chatId]);

  // Handle reply
  const handleReply = useCallback((message: Message) => {
    setReplyingTo(message);
  }, []);

  // Handle reaction
  const handleReact = useCallback(
    async (messageId: string, reaction: string) => {
      try {
        const response = await retryFetch(
          `${config.API_BASE}/api/messages/${messageId}/reactions`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ reaction }),
          }
        );

        if (!response.ok) {
          // Handle HTTP error (e.g., 400/500)
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || `Request failed with ${response.status}`
          );
        }

        const data = await response.json();
        setMessages((prev) =>
          prev.map((msg) => (msg._id === messageId ? data.item : msg))
        );
      } catch (error) {
        console.error("Error while reacting to message:", error);
        toast.error(t("failed_to_add_reaction"));
      }
    },
    [retryFetch, t]
  );

  const handleTyping = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;

    setNewMessage(value);

    // If user clears input completely, immediately stop typing

    if (!value.trim() && isTyping && isConnected && chatId) {
      sendTypingStop(chatId);
      setIsTyping(false);
    }
  };

  useEffect(() => {
    return () => {
      if (isConnected && chatId && isTyping) {
        sendTypingStop(chatId);
        console.log("🧹 Component cleanup: stopped typing");
      }
    };
  }, [isConnected, chatId, isTyping, sendTypingStop]);

  // Clear typing indicator when user changes
  useEffect(() => {
    if (currentUserId) {
      // Reset typing state when switching chats
      setIsTyping(false);
    }
  }, [currentUserId]);

  // Handle send message
  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim() || !chatId) return;

    if (isConnected && isTyping) {
      sendTypingStop(chatId);

      setIsTyping(false);
    }

    const messageText = newMessage.trim();
    setNewMessage("");

    console.log("[ProcessChat] currentPaneReference before sending:", currentPaneReference);

    try {
      const messagePayload = {
        content: messageText,
        replyTo: replyingTo ? replyingTo._id : null,
        ...(currentPaneReference && {
          referenceType: "screen",
          reference: {
            screenId: currentPaneReference.screen,
            screenLabel: currentPaneReference.screenLabel,
            componentId: currentPaneReference.componentKey,
            componentLabel: currentPaneReference.component,
          },
        }),
      };

      console.log("[ProcessChat] Sending message with payload:", messagePayload);
      console.log("[ProcessChat] Reference in payload:", messagePayload.reference);

      const response = await retryFetch(
        `${config.API_BASE}/api/chats/${chatId}/messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(messagePayload),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to send message: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();
      setMessages((prev) => {
        // Deduplication check before adding sent message
        if (prev.some(m => m._id === data.item._id)) {
          return prev;
        }
        return [data.item, ...prev];
      });
      setReplyingTo(null);

      // Clear pane reference after sending
      if (currentPaneReference) {
        setCurrentPaneReference(null);
        onPaneReferenceUsed?.();
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(t("failed_to_send_message"));
      setNewMessage(messageText); // Restore message on error
    }
  }, [
    newMessage,
    chatId,
    isConnected,
    sendMessage,
    replyingTo,
    currentUserId,
    retryFetch,
    t,
  ]);

  // Handle typing
  // const handleTyping = useCallback(
  //   (e: React.ChangeEvent<HTMLTextAreaElement>) => {
  //     setNewMessage(e.target.value);

  //     // Send typing start/stop via socket
  //     if (isConnected && chatId) {
  //       if (e.target.value.trim()) {
  //         sendTypingStart(chatId);
  //         debouncedTypingStop(chatId);
  //       } else {
  //         sendTypingStop(chatId);
  //       }
  //     }
  //   },
  //   [isConnected, chatId, sendTypingStart, sendTypingStop, debouncedTypingStop]
  // );

  // Handle formatting
  const handleFormat = useCallback(
    (type: "bold" | "italic" | "list" | "code") => {
      const result = insertMarkdown(textareaRef.current, newMessage, type);
      setNewMessage(result.newText);

      // Set cursor position after React updates
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(result.newCursorPos, result.newCursorPos);
        }
      }, 0);
    },
    [newMessage]
  );

  // Handle key press
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Handle markdown shortcuts
      const shortcutResult = handleMarkdownShortcut(e, textareaRef.current, newMessage);
      if (shortcutResult) {
        setNewMessage(shortcutResult.newText);
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.setSelectionRange(
              shortcutResult.newCursorPos,
              shortcutResult.newCursorPos
            );
          }
        }, 0);
        return;
      }

      // Handle Enter to send
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage, newMessage]
  );

  // Clear typing indicator when component unmounts
  useEffect(() => {
    return () => {
      if (isConnected && chatId) {
        sendTypingStop(chatId);
      }
    };
  }, [isConnected, chatId, sendTypingStop]);

  // Ensure scroll is at bottom when chat opens or new messages are added (not during pagination)
  useEffect(() => {
    const messagesContainer = document.querySelector(".messages-container");
    if (messagesContainer && messages.length > 0 && shouldScrollToBottom) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      });
    }
  }, [messages.length, chatId, shouldScrollToBottom]);

  // Rendering functions for mentoring UI
  function mentorDisplayName() {
    const email = process?.fiExpert?.email ?? "N/A";
    const displayName = vendor?.displayName || process?.fiExpert?.displayName || email.split("@")[0];

    return (
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center">
          <img
            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random`}
            alt="Mentor Avatar"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-base">{displayName}</h4>
          <p className="text-sm text-muted-foreground">{t("mentor")}</p>
        </div>
      </div>
    );
  }

  function renderLoadingIndicator() {
    if (isLoadingMentoring || isLoadingMentoringData) {
      return (
        <div className="flex items-center justify-center py-4">
          <Spinner size="md" />
        </div>
      );
    }
    return null;
  }

  function availableCredits() {
    if (unlimitedMentoring) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <UserPlus className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-green-900">{t("unlimited_mentoring")}</p>
              <p className="text-sm text-green-700">{t("unlimited_mentoring_access")}</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`border rounded-lg p-4 ${mentorCredits > 0 ? 'bg-primary/5 border-primary/20' : 'bg-yellow-50 border-yellow-200'}`}>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full ${mentorCredits > 0 ? 'bg-primary/10' : 'bg-yellow-100'} flex items-center justify-center`}>
            <span className={`font-bold ${mentorCredits > 0 ? 'text-primary' : 'text-yellow-600'}`}>{mentorCredits}</span>
          </div>
          <div>
            <p className={`font-medium ${mentorCredits > 0 ? 'text-primary' : 'text-yellow-900'}`}>
              {t("mentoring_credits")}
            </p>
            <p className={`text-sm ${mentorCredits > 0 ? 'text-primary' : 'text-yellow-700'}`}>
              {mentorCredits > 0
                ? t(mentorCredits === 1 ? "mentor_sessions_available" : "mentor_sessions_available_plural", { count: mentorCredits })
                : t("no_credits_available")
              }
            </p>
          </div>
        </div>
      </div>
    );
  }

  function inviteMentorButton() {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <h4 className="font-medium text-sm">{t("message")} ({t("optional")})</h4>
          <Textarea
            placeholder={t("add_message_to_mentor")}
            value={mentorMessage}
            onChange={(e) => setMentorMessage(e.target.value)}
            className="resize-none min-h-[100px]"
          />
        </div>

        <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
          <p className="font-medium">{t("what_happens_next")}</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>• {t("mentor_will_be_notified")}</li>
            {!unlimitedMentoring && <li>• {t("session_credit_deducted_on_accept")}</li>}
            <li>• {t("session_lasts_until_engagement_ends")}</li>
            <li>• {t("chat_with_mentor_realtime")}</li>
          </ul>
        </div>

        <Button
          className="w-full"
          size="lg"
          onClick={() => {
            inviteMentor();
          }}
          disabled={isLoadingMentoring}
        >
          {isLoadingMentoring ? (
            <>
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
              {t("sending_invitation")}
            </>
          ) : (
            <>
              <UserPlus className="h-5 w-5 mr-2" />
              {t("send_invitation")}
            </>
          )}
        </Button>
      </div>
    );
  }

  function buyMentorCreditsView() {
    if (!Array.isArray(mentorProducts)) {
      return <PageLoader label={t("loading_packages")} />;
    }

    if (mentorProducts.length === 0) {
      return (
        <div className="text-center py-8 space-y-2">
          <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto opacity-50" />
          <p className="text-sm text-muted-foreground">{t("no_mentorship_packages_available")}</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <h4 className="font-medium text-sm">{t("available_mentor_credit_packages")}</h4>
        <div className="grid gap-4 md:grid-cols-2">
          {mentorProducts.map((product, i) => (
            <div
              key={product._id}
              className="border rounded-lg p-4 space-y-3 hover:border-primary transition-colors"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold">{product.data.mentor_session_credit.amount}</span>
                    <span className="text-sm text-muted-foreground">
                      {product.data.mentor_session_credit.amount === 1 ? t("session") : t("sessions")}
                    </span>
                  </div>
                  <p className="text-lg font-semibold mt-1">
                    {currencyAmount(product.price.amount, product.price.currency)}
                  </p>
                </div>
                <ShoppingCart className="h-5 w-5 text-muted-foreground" />
              </div>

              <Button
                className="w-full"
                onClick={() => {
                  buyMentorCredits(i);
                }}
                disabled={isLoadingMentoring}
              >
                {isLoadingMentoring ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    {t("processing")}
                  </>
                ) : (
                  <>
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    {t("purchase")}
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!processId) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p>{t("no_process_selected")}</p>
      </div>
    );
  }

  if (isCreatingChat) {
    return <PageLoader label={t("creating_chat")} />;
  }

  // Show loading state while checking mentor status
  if (isLoadingProcess) {
    return (
      <div className="flex flex-col h-full min-h-0" dir="ltr">
        <div className="flex items-center justify-center p-8 h-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-8 h-8 text-muted-foreground animate-pulse" />
            </div>
            <h3 className="font-medium mb-2">{t("loading")}</h3>
            <p className="text-sm text-muted-foreground">
              {t("checking_mentor_status")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show mentor approval UI for mentors who have been invited but haven't joined yet
  // Skip if user is running their own workflow
  if (isMentor && isMentorInvited && !isMentorJoined && !isOwnWorkflow) {
    return (
      <div className="flex flex-col h-full min-h-0" dir="ltr">
        <div className="flex items-center p-4 border-b bg-background/95 backdrop-blur-sm flex-shrink-0 gap-3">
          <div className="flex-1">
            <div className="font-medium flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              {otherUser ? (
                <>
                  {t("chat_with_mentee") || "Chat with Client"}: {otherUser.displayName}
                </>
              ) : (
                t("process_chat")
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              {t("invitation_pending") || "Invitation Pending"}
            </div>
          </div>
          {!isMobile && onCollapse && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCollapse}
              className="h-8 w-8 p-0"
              title="Collapse chat"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-6 max-w-md">
              <div className="w-20 h-20 bg-primary/5 border-2 border-primary/20 rounded-full flex items-center justify-center mx-auto">
                <UserPlus className="w-10 h-10 text-primary" />
              </div>

              {/* Client Info */}
              {otherUser && (
                <div className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-start gap-3">
                    <div className="h-12 w-12 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center">
                      <img
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(otherUser.displayName)}&background=random`}
                        alt="Client Avatar"
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="font-medium text-base">{otherUser.displayName}</h4>
                      <p className="text-sm text-muted-foreground">{t("client") || "Client"}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <h3 className="text-lg font-semibold">
                  {t("client_has_requested_mentoring") || "Client has requested mentoring for this engagement"}
                </h3>

                {/* Display client's message if available */}
                {processData?.invitationMessage && (
                  <div className="bg-muted/50 rounded-lg p-4 text-left">
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      {t("message_from_client") || "Message from client:"}
                    </p>
                    <p className="text-sm italic">"{processData.invitationMessage}"</p>
                  </div>
                )}
              </div>

              <Button
                onClick={approveMentoringRequest}
                disabled={isLoadingMentoring}
                size="lg"
                className="w-full bg-primary hover:bg-primary/90"
              >
                {isLoadingMentoring ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    {t("accepting_invitation") || "Accepting Invitation..."}
                  </>
                ) : (
                  <>
                    <UserPlus className="h-5 w-5 mr-2" />
                    {t("accept_invitation") || "Accept Invitation"}
                  </>
                )}
              </Button>

              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm text-left">
                <p className="font-medium">{t("what_happens_when_you_accept") || "What happens when you accept:"}</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• {t("you_will_have_access_to_engagement") || "You'll have access to view the client's engagement data"}</li>
                  <li>• {t("you_can_chat_with_client") || "You can chat with the client in real-time"}</li>
                  {!unlimitedMentoring && <li>• {t("one_credit_will_be_deducted") || "One mentoring credit will be deducted"}</li>}
                  <li>• {t("session_lasts_until_engagement_ends") || "The session lasts until the engagement ends"}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show mentoring request UI only on /activities/run page (when onPaneReferenceUsed is present)
  // if mentor hasn't been INVITED yet AND unlimited mentoring is not enabled AND user is NOT the mentor
  // Skip if user is running their own workflow
  // Once mentor is invited, show normal chat UI (even if they haven't joined yet)
  if (onPaneReferenceUsed && !isMentorInvited && !unlimitedMentoring && !isMentor && !isOwnWorkflow) {
    return (
      <>
        <div className="flex flex-col h-full min-h-0" dir="ltr">
          <div className="flex items-center p-4 border-b bg-background/95 backdrop-blur-sm flex-shrink-0 gap-3">
            <div className="flex-1">
              <div className="font-medium flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                {t("process_chat")}
              </div>
              <div className="text-sm text-muted-foreground">
                {t("mentor_not_available")}
              </div>
            </div>
            {!isMobile && onCollapse && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCollapse}
                className="h-8 w-8 p-0"
                title="Collapse chat"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Initial State: Mentor has not joined yet */}
            {!showMentoringForm && !isMentorInvited && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center space-y-6 max-w-md">
                  <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto">
                    <MessageCircle className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold">{t("mentor_not_joined_title")}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t("mentor_not_joined_message")}
                    </p>
                  </div>
                  <Button
                    onClick={handleRequestMentoring}
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    <UserPlus className="h-5 w-5 mr-2" />
                    {t("label_request_mentoring")}
                  </Button>
                </div>
              </div>
            )}

            {/* Mentoring Form: Show credits and message input */}
            {showMentoringForm && !isMentorInvited && (
              <div className="space-y-6 max-w-2xl mx-auto">
                {isLoadingMentoringData ? (
                  <PageLoader label={`${t("loading")}...`} />
                ) : mentoringDataError ? (
                  <div className="text-center py-12 space-y-4">
                    <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
                      <MessageCircle className="w-8 h-8 text-destructive" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-medium text-destructive">{t("error_loading_information")}</h3>
                      <p className="text-sm text-muted-foreground">{mentoringDataError}</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => getProcessInfo()}
                    >
                      {t("try_again")}
                    </Button>
                  </div>
                ) : process ? (
                  <>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold">
                          {t("label_request_mentoring")}
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowMentoringForm(false)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Mentor Info Card */}
                      <div className="border rounded-lg p-4 bg-muted/30">
                        {mentorDisplayName()}
                      </div>

                      {/* Credits Display */}
                      {availableCredits()}

                      {/* Divider */}
                      <div className="border-t my-4" />

                      {/* Invitation Form or Purchase */}
                      <div className="space-y-4">
                        {unlimitedMentoring || mentorCredits > 0 ? (
                          inviteMentorButton()
                        ) : (
                          <div className="space-y-4">
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                              <p className="text-sm text-yellow-800">
                                {t("need_credits_to_request_mentoring")}
                              </p>
                            </div>
                            {buyMentorCreditsView()}
                          </div>
                        )}
                      </div>
                    </div>

                    {renderLoadingIndicator()}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-sm text-muted-foreground">{t("no_mentor_information_available")}</p>
                  </div>
                )}
              </div>
            )}

            {/* Chat is created after mentor is invited - redirect to main chat UI */}
            {isMentorInvited && chatId && null /* This state is now handled by the main chat UI below with a banner */}
          </div>
        </div>

        <PaymentIframe
          open={showPaymentIframe}
          paymentUrl={paymentUrl}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onClose={handlePaymentClose}
        />
      </>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 w-full transition-all duration-300" dir={i18n.dir()}>
      {/* Chat header */}
      <div className="flex items-center p-4 border-b bg-background/95 backdrop-blur-sm flex-shrink-0 gap-3">
        <div className="flex-1">
          <div className="font-medium flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            {otherUser ? (
              isMentor ? (
                <>
                  {t("chat_with_mentee") || "Chat with Client"}: {otherUser.displayName}
                </>
              ) : (
                <>
                  {t("chat_with_mentor") || "Chat with Mentor"}: {otherUser.displayName}
                </>
              )
            ) : (
              t("process_chat")
            )}
            {/* Status Badge */}
            {isMentorJoined !== null && !isOwnWorkflow && (
              <Badge variant={isMentorJoined ? "default" : "secondary"} className="ml-2">
                {isMentor
                  ? (isMentorJoined ? t("session_active") || "Active" : t("invitation_pending") || "Pending")
                  : (isMentorJoined ? t("label_mentor_joined") : t("label_mentor_invited_to_process"))
                }
              </Badge>
            )}
          </div>
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            {otherUser && (
              <OnlineStatusIndicator userId={otherUser._id} showText={true} isUserOnline={isConnected} />
            )}
            {isOtherUserTyping && otherTypingUserName && (
              <span className="text-primary italic">• {otherTypingUserName} {t("is_typing")}...</span>
            )}
          </div>
        </div>
        {!isMobile && onCollapse && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onCollapse}
            className="h-8 w-8 p-0"
            title="Collapse chat"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Approval Banner for Mentors */}
      {isMentor && isMentorInvited && !isMentorJoined && !isOwnWorkflow && (
        <div className="bg-primary/5 border-b border-primary/20 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <h4 className="font-medium text-primary mb-1">
                {t("invitation_from_client") || "Invitation from Client"}
              </h4>
              <p className="text-sm text-primary">
                {otherUser?.displayName || t("a_mentee")} {t("has_invited_you_to_this_session") || "has invited you to join this session"}
              </p>
            </div>
            <Button
              onClick={approveMentoringRequest}
              disabled={isLoadingMentoring}
              className="bg-primary hover:bg-primary/90"
            >
              {isLoadingMentoring ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  {t("accepting_invitation") || "Accepting Invitation..."}
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t("accept_invitation") || "Accept Invitation"}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Waiting Banner for Mentees - Mentor invited but hasn't joined yet */}
      {!isMentor && isMentorInvited && !isMentorJoined && !isOwnWorkflow && !unlimitedMentoring && (
        <div className="bg-amber-50 border-b border-amber-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-amber-900 mb-1">
                {t("waiting_for_mentor") || "Waiting for Mentor"}
              </h4>
              <p className="text-sm text-amber-700">
                {t("mentor_notified_can_send_messages") || "Your mentor has been notified. You can send messages now, and they'll see them once they accept."}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => inviteMentor()}
              disabled={isLoadingMentoring}
              className="flex-shrink-0"
            >
              {isLoadingMentoring ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
                  {t("sending")}...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-1" />
                  {t("resend") || "Resend"}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto messages-container min-h-0">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center p-8 h-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-medium mb-2">
                {t("start_process_conversation")}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t("process_conversation_beginning")}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col-reverse space-y-4 pt-2 px-4">
            {messages.map((message, index) => (
              <div key={message._id} className={index === 0 ? "my-4" : "mb-8"}>
                <MessageBubble
                  message={message}
                  isOwn={message.direction === "sent"}
                  onReply={handleReply}
                  onReact={handleReact}
                  index={index}
                  processId={processId}
                  onPaneReferenceClick={onPaneReferenceClick}
                />
              </div>
            ))}
            <div ref={topRef} />
            {isLoading && (
              <div className="text-center py-2 text-sm text-gray-500">
                {t("loading_more")}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Typing indicator */}
      <TypingIndicator
        isVisible={isOtherUserTyping}
        userName={otherTypingUserName}
      />

      {/* Reply preview */}
      {replyingTo && (
        <div
          className={cn(
            "bg-muted/50 border-t flex-shrink-0 px-4 py-2",
            isRTL
              ? "border-r-4 border-r-primary" // RTL: right border
              : "border-l-4 border-l-primary" // LTR: left border
          )}
        >
          <div
            className={`flex items-center justify-between ${
              isRTL ? "flex-row-reverse" : "flex-row"
            }`}
          >
            <div>
              <div className="text-xs font-medium text-muted-foreground">
                {t("replying_to")}{" "}
                {replyingTo.senderId === "current"
                  ? t("yourself")
                  : replyingTo.senderName || t("someone")}
              </div>
              <div
                className={`text-sm truncate ${
                  isRTL ? "text-right" : "text-left"
                }`}
              >
                {replyingTo.text}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setReplyingTo(null)}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Message input */}
      <div className="border-t bg-background/95 backdrop-blur-sm flex-shrink-0 p-4 pb-6 min-h-[80px]">
        {/* Pane Reference Preview */}
        {currentPaneReference && (
          <div className="mb-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">
                  {t("quoting_pane")}: {currentPaneReference.screenLabel}
                  {currentPaneReference.component && (
                    <span className="text-primary ml-1">
                      → {currentPaneReference.component}
                    </span>
                  )}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-primary hover:bg-primary/10"
                onClick={() => {
                  setCurrentPaneReference(null);
                  onPaneReferenceUsed?.();
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <FormattingToolbar onFormat={handleFormat} />
          <div className="flex items-end gap-2">
            <div className="flex-1 relative">
              <Textarea
                ref={textareaRef}
                value={newMessage}
                onChange={handleTyping}
                onKeyDown={handleKeyPress}
                placeholder={t("type_message")}
                className="min-h-[44px] max-h-32 resize-none text-base"
                rows={1}
              />
            </div>
            <Button
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
              size="sm"
              className="h-10 w-10 p-0"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProcessChat;
